import { createAction, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'
import { type AppState } from 'src/redux/app/app.store'
import { type Area, type AreaType, type FeatureCollection } from 'src/api/fm/zones/zones.model'

export enum LoadState {
    NotStarted = 'NotStarted',
    InProgress = 'InProgress',
    Success = 'Success',
    Failed = 'Failed',
}

interface ZoneAreasState {
    areas: FeatureCollection | undefined
    subZones: Area[]
    loadState: LoadState
}

export const requestFetchAreas = createAction<string>('zoneAreas/requestFetchAreas')

const initialState: ZoneAreasState = { areas: undefined, subZones: [], loadState: LoadState.NotStarted }

const zoneAreasSlice = createSlice({
    name: 'zoneAreas',
    initialState,
    reducers: {
        fetchAreasStart(state) {
            return { ...state, areas: undefined, loadState: LoadState.InProgress }
        },
        fetchAreasSuccess(state, action: PayloadAction<FeatureCollection>) {
            return { ...state, areas: action.payload, loadState: LoadState.Success }
        },
        fetchAreasFailed(state) {
            return { ...state, areas: undefined, loadState: LoadState.Failed }
        },
        fetchSubZonesStart(state) {
            return { ...state, subZones: [] }
        },
        fetchSubZonesSuccess(state, action: PayloadAction<Area[]>) {
            return { ...state, subZones: action.payload }
        },
        resetAreas(_) {
            return initialState
        },
    },
})

export const {
    fetchAreasStart,
    fetchAreasSuccess,
    fetchAreasFailed,
    fetchSubZonesStart,
    fetchSubZonesSuccess,
    resetAreas,
} = zoneAreasSlice.actions

const selectZoneAreas = (state: AppState): FeatureCollection | undefined => state.zoneAreas.areas
const selectSubZones = (state: AppState): Area[] => state.zoneAreas.subZones

const DEFAULT_AREAS_BY_TYPE: Record<AreaType, Area[]> = {
    operations: [],
    bounty: [],
    'parking-spot': [],
    'no-parking': [],
    'slow-zone': [],
    'no-riding': [],
    'no-start': [],
    incentive: [],
    'fleet-capacity': [],
    'soft-mpz': [],
    'free-floating': [],
    'sub-zone': [],
}

export const selectZoneAreasByType = createSelector([selectZoneAreas, selectSubZones], (zoneAreas, subZones) => {
    const features = [...(zoneAreas?.features ?? []), ...subZones]
    if (features.length === 0) {
        return DEFAULT_AREAS_BY_TYPE
    }

    return features.reduce((acc, area) => {
        const { area_type } = area.properties
        const newAreasForType = [...acc[area_type], area]

        return {
            ...acc,
            [area_type]: newAreasForType,
        }
    }, DEFAULT_AREAS_BY_TYPE)
})

export const { reducer: zoneAreasReducer } = zoneAreasSlice
